import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ApiService } from '../../../../../../core/services/api.service';
import { ApiRegisterMemberListService } from '../../../../../../core/services/api/cityparking/api-register-member-list.service';
import { MapperRegisterMemberListService } from '../../../../../../core/mappers/cityparking/mapper-register-member-list.service';
import {
  ActionBackOfficeResponse,
  RegisterMemberByIdDto,
  RegisterMemberVehicleOptionDto,
  UpdateRegisterStatusPayload,
} from '../../../../../../core/models/cityparking/register-member-list.model';
import { FeeDetailsResponse } from '../../../../../../core/models/parking.model';
import { PssConfirmModalComponent } from '../../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-register-member-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, PssConfirmModalComponent, PssLoadingComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-register-member-detail.component.html',
  styleUrl: './modal-register-member-detail.component.css',
  providers: [DatePipe],
})
export class ModalRegisterMemberDetailComponent implements OnInit {
  @Input() id = 0;
  @Input() siteId = 0;
  @Input() userId = 0;
  @Input() menuId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  data: RegisterMemberByIdDto | null = null;
  isLoading = false;
  isSaving = false;
  isVisible = false;

  editMode = false;
  phone = '';
  remarks = '';
  selectedFeeId: number | null = null;
  selectedVehicleId: number | null = null;

  feeOptions: { value: number; label: string; subscriptionId: number }[] = [];
  vehicleOptions: { vehicleId: number; label: string }[] = [];

  // Cancellation modal and processing state variables
  showCancelModal = false;
  isCancelling = false;

  constructor(
    private readonly api: ApiRegisterMemberListService,
    private readonly legacyApi: ApiService,
    private readonly mapper: MapperRegisterMemberListService,
    private readonly toast: ToastService,
    private readonly datePipe: DatePipe,
  ) { }

  // ================= Normal Functions =================

  /**
   * Initializes the component, sets visibility for animation, and loads member details.
   */
  ngOnInit(): void {
    setTimeout(() => (this.isVisible = true), 10);
    if (this.id) {
      this.loadDetail();
    }
  }

  /**
   * Getter property that checks if the record can be edited.
   */
  get canEdit(): boolean {
    return Boolean(this.data?.canEdit);
  }

  /**
   * Maps status code to localized status text labels.
   */
  statusLabel(displayStatus: number): string {
    return this.mapper.getDisplayStatusLabel(displayStatus);
  }

  /**
   * Maps status code to CSS utility classes for styling the status badge.
   */
  statusClass(displayStatus: number): string {
    return this.mapper.getDisplayStatusClass(displayStatus);
  }

  /**
   * Formats string values representing date and time into 'dd/MM/yyyy HH:mm'.
   */
  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    return this.datePipe.transform(value, 'dd/MM/yyyy HH:mm') || '-';
  }

  /**
   * Closes the modal with a fade-out animation and emits close event.
   */
  closeModal(): void {
    this.isVisible = false;
    setTimeout(() => this.close.emit(), 200);
  }

  /**
   * Transitions component into edit mode and loads select options if allowed.
   */
  startEdit(): void {
    if (!this.canEdit) {
      this.toast.warn('รายการนี้ไม่สามารถแก้ไขได้');
      return;
    }
    this.editMode = true;
    this.loadFeeOptions();
    this.loadVehicleOptions();
  }

  /**
   * Reverts current edit actions and returns to display mode.
   */
  cancelEdit(): void {
    this.editMode = false;
    this.applyFormFromData();
  }

  /**
   * Resets select options and values mapping from component local data.
   */
  private applyFormFromData(): void {
    if (!this.data) return;
    this.phone = this.data.phone ?? '';
    this.remarks = this.data.remarks ?? '';
    this.selectedFeeId = this.data.feeId || null;
    this.selectedVehicleId = this.data.cpVehicleId || null;
  }

  /**
   * Displays the cancellation confirmation modal.
   */
  onCancel(): void {
    this.showCancelModal = true;
  }

  /**
   * Hides the cancellation confirmation modal.
   */
  closeCancelModal(): void {
    this.showCancelModal = false;
  }

  // ================= API Functions =================

  /**
   * Validates form input values and makes API call to update record.
   */
  saveEdit(): void {
    if (!this.data?.id || !this.userId || !this.menuId) {
      this.toast.error('ข้อมูลไม่ครบถ้วน');
      return;
    }
    if (!this.selectedVehicleId) {
      this.toast.warn('กรุณาเลือกทะเบียนรถ');
      return;
    }

    const selectedFee = this.feeOptions.find((f) => f.value === this.selectedFeeId);
    this.isSaving = true;

    this.api
      .updateRegisterMember({
        id: this.data.id,
        userId: this.userId,
        menuId: this.menuId,
        phone: this.phone,
        remarks: this.remarks,
        feeId: this.selectedFeeId ?? undefined,
        subscriptionId: selectedFee?.subscriptionId,
        cpVehicleId: this.selectedVehicleId,
      })
      .subscribe({
        next: (res: ActionBackOfficeResponse) => {
          const raw = res as ActionBackOfficeResponse & { IsSuccess?: boolean; Message?: string };
          const ok = raw.isSuccess ?? raw.IsSuccess ?? false;
          if (!ok) {
            this.toast.error(raw.message ?? raw.Message ?? 'บันทึกไม่สำเร็จ');
            this.isSaving = false;
            return;
          }
          this.toast.success('บันทึกการแก้ไขสำเร็จ');
          this.isSaving = false;
          this.save.emit();
        },
        error: (err: unknown) => {
          console.error('[API Error] updateRegisterMember:', err);
          const errObj = err as { error?: { message?: string; Message?: string } };
          const msg = errObj?.error?.message ?? errObj?.error?.Message ?? 'บันทึกไม่สำเร็จ';
          this.toast.error(msg);
          this.isSaving = false;
        },
      });
  }

  /**
   * Loads the register member details object by membership ID from the API.
   */
  private loadDetail(): void {
    this.isLoading = true;
    this.api.getRegisterMemberById(this.id, this.userId).subscribe({
      next: (res: RegisterMemberByIdDto) => {
        this.data = this.mapper.normalizeDetail(res);
        this.applyFormFromData();
        this.isLoading = false;
      },
      error: (err: unknown) => {
        console.error('[API Error] getRegisterMemberById:', err);
        this.toast.error('ไม่สามารถโหลดรายละเอียดได้');
        this.isLoading = false;
        this.closeModal();
      },
    });
  }

  /**
   * Fetches available subscription fee packages options list from backend service.
   */
  private loadFeeOptions(): void {
    if (!this.siteId) return;
    this.legacyApi.getFeeDetails(this.siteId).subscribe({
      next: (fees: FeeDetailsResponse[]) => {
        this.feeOptions = fees.map((fee) => ({
          value: fee.id,
          label: `${fee.name} (${fee.fee} บาท)`,
          subscriptionId: fee.subscription_id,
        }));
      },
      error: (err: unknown) => console.error('[API Error] getFeeDetails:', err),
    });
  }

  /**
   * Fetches registered vehicle options corresponding to user member profile.
   */
  private loadVehicleOptions(): void {
    const cpUserId = this.data?.cpUserId ?? 0;
    if (!cpUserId || !this.siteId) return;

    this.api.getRegisterMemberVehiclesByCpUserId(cpUserId, this.userId, this.siteId).subscribe({
      next: (list: RegisterMemberVehicleOptionDto[]) => {
        this.vehicleOptions = (list ?? []).map((item: RegisterMemberVehicleOptionDto) => {
          const normalized = this.mapper.normalizeVehicleOption(item);
          return { vehicleId: normalized.vehicleId, label: normalized.label };
        });
        if (this.vehicleOptions.length === 0) {
          this.toast.warn('ไม่พบทะเบียนรถของผู้ใช้นี้');
        }
      },
      error: (err: unknown) => console.error('[API Error] getRegisterMemberVehiclesByCpUserId:', err),
    });
  }

  /**
   * Triggered upon confirmation from user to execute record cancel call.
   */
  confirmCancel(): void {
    if (!this.userId) {
      this.toast.error('ผู้ใช้งานไม่ถูกต้อง');
      return;
    }
    const id = this.data?.id || this.id;
    if (!id) {
      this.toast.error('ไม่พบรหัสรายการสำหรับยกเลิก');
      return;
    }

    this.isCancelling = true;
    this.closeCancelModal();

    const payload: UpdateRegisterStatusPayload = {
      id,
      status: 2,
      userId: this.userId,
      menuId: this.menuId,
    };

    this.api.updateRegisterStatus(payload).subscribe({
      next: (res: ActionBackOfficeResponse) => {
        console.log("UpdateRegisterStatus : ", res)
        this.isCancelling = false;
        if (res.isSuccess) {
          this.toast.success('ยกเลิกรายการเรียบร้อย');
          this.save.emit();
          this.closeModal();
        } else {
          this.toast.error(res.message || 'ยกเลิกรายการไม่สำเร็จ');
        }
      },
      error: (err: unknown) => {
        this.isCancelling = false;
        console.error('[API Error] updateRegisterStatus:', err);
        this.toast.error('เกิดข้อผิดพลาดในการยกเลิกรายการ');
      },
    });
  }

  openActivityHistory(): void {
    if (!this.id) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
